import { LocalNotifications, ScheduleOptions } from "@capacitor/local-notifications";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";

export interface NotificationSchedule {
  meals: { type: string; label: string; time: string; message: string }[];
  hydration: { intervalMinutes: number; startHour: number; endHour: number; message: string };
  exercise: { time: string; message: string } | null;
}

const STORAGE_KEY = "nutrете_notif_enabled";
const SCHEDULE_KEY = "nutrете_notif_schedule";

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export function areNotificationsEnabled(): boolean {
  return localStorage.getItem(STORAGE_KEY) === "true";
}

export function setNotificationsEnabled(enabled: boolean) {
  localStorage.setItem(STORAGE_KEY, String(enabled));
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!isNativePlatform()) return false;

  try {
    const result = await LocalNotifications.requestPermissions();
    return result.display === "granted";
  } catch (e) {
    console.error("Error requesting notification permissions:", e);
    return false;
  }
}

export async function checkNotificationPermission(): Promise<boolean> {
  if (!isNativePlatform()) return false;

  try {
    const result = await LocalNotifications.checkPermissions();
    return result.display === "granted";
  } catch {
    return false;
  }
}

export async function fetchSmartSchedule(): Promise<NotificationSchedule | null> {
  try {
    const { data, error } = await supabase.functions.invoke("smart-notifications");
    if (error || data?.error) {
      console.error("Error fetching smart schedule:", error || data?.error);
      return null;
    }
    return data as NotificationSchedule;
  } catch (e) {
    console.error("Error fetching smart schedule:", e);
    return null;
  }
}

export async function scheduleAllNotifications(schedule: NotificationSchedule) {
  if (!isNativePlatform()) return;

  try {
    // Clear existing notifications first
    const pending = await LocalNotifications.getPending();
    if (pending.notifications.length > 0) {
      await LocalNotifications.cancel({ notifications: pending.notifications });
    }

    const notifications: ScheduleOptions["notifications"] = [];
    let idCounter = 1;

    // Schedule meal reminders
    for (const meal of schedule.meals) {
      const [hour, minute] = meal.time.split(":").map(Number);
      if (isNaN(hour) || isNaN(minute)) continue;

      // Schedule for the next 7 days
      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const triggerDate = new Date();
        triggerDate.setDate(triggerDate.getDate() + dayOffset);
        triggerDate.setHours(hour, minute, 0, 0);

        // Skip if in the past
        if (triggerDate.getTime() < Date.now()) continue;

        notifications.push({
          id: idCounter++,
          title: `🍽️ ${meal.label}`,
          body: meal.message,
          schedule: { at: triggerDate },
          sound: "default",
          smallIcon: "ic_stat_icon_config_sample",
          iconColor: "#4CAF50",
        });
      }
    }

    // Schedule hydration reminders
    const { intervalMinutes, startHour, endHour, message: hydroMsg } = schedule.hydration;
    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
      let currentHour = startHour;
      let currentMinute = 0;

      while (currentHour < endHour) {
        const triggerDate = new Date();
        triggerDate.setDate(triggerDate.getDate() + dayOffset);
        triggerDate.setHours(currentHour, currentMinute, 0, 0);

        if (triggerDate.getTime() > Date.now()) {
          notifications.push({
            id: idCounter++,
            title: "💧 Hora de hidratarte",
            body: hydroMsg,
            schedule: { at: triggerDate },
            sound: "default",
            smallIcon: "ic_stat_icon_config_sample",
            iconColor: "#2196F3",
          });
        }

        currentMinute += intervalMinutes;
        while (currentMinute >= 60) {
          currentMinute -= 60;
          currentHour++;
        }
      }
    }

    // Schedule exercise reminder
    if (schedule.exercise) {
      const [exHour, exMinute] = schedule.exercise.time.split(":").map(Number);
      if (!isNaN(exHour) && !isNaN(exMinute)) {
        for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
          const triggerDate = new Date();
          triggerDate.setDate(triggerDate.getDate() + dayOffset);
          triggerDate.setHours(exHour, exMinute, 0, 0);

          if (triggerDate.getTime() > Date.now()) {
            notifications.push({
              id: idCounter++,
              title: "🏋️ Hora de ejercitarte",
              body: schedule.exercise.message,
              schedule: { at: triggerDate },
              sound: "default",
              smallIcon: "ic_stat_icon_config_sample",
              iconColor: "#FF9800",
            });
          }
        }
      }
    }

    if (notifications.length > 0) {
      // Capacitor has a limit, schedule in batches
      const batchSize = 60;
      for (let i = 0; i < notifications.length; i += batchSize) {
        const batch = notifications.slice(i, i + batchSize);
        await LocalNotifications.schedule({ notifications: batch });
      }
    }

    // Cache the schedule
    localStorage.setItem(SCHEDULE_KEY, JSON.stringify({ schedule, updatedAt: Date.now() }));

    return notifications.length;
  } catch (e) {
    console.error("Error scheduling notifications:", e);
    return 0;
  }
}

export async function cancelAllNotifications() {
  if (!isNativePlatform()) return;

  try {
    const pending = await LocalNotifications.getPending();
    if (pending.notifications.length > 0) {
      await LocalNotifications.cancel({ notifications: pending.notifications });
    }
    localStorage.removeItem(SCHEDULE_KEY);
  } catch (e) {
    console.error("Error canceling notifications:", e);
  }
}

export async function refreshNotificationsIfNeeded() {
  if (!isNativePlatform() || !areNotificationsEnabled()) return;

  try {
    const cached = localStorage.getItem(SCHEDULE_KEY);
    if (cached) {
      const { updatedAt } = JSON.parse(cached);
      // Refresh every 24 hours
      if (Date.now() - updatedAt < 24 * 60 * 60 * 1000) return;
    }

    const schedule = await fetchSmartSchedule();
    if (schedule) {
      await scheduleAllNotifications(schedule);
    }
  } catch (e) {
    console.error("Error refreshing notifications:", e);
  }
}
